import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface AccountRelatedDataCount {
  transactions: number;
  cards: number;
  futureReleases: number;
  installmentGroups: number;
  total: number;
}

/**
 * Get count of all data related to an account
 */
export async function getAccountRelatedDataCount(accountId: string): Promise<AccountRelatedDataCount> {
  try {
    const [transactions, cards, futureReleases, installmentGroups] = await Promise.all([
      supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('account_id', accountId),
      supabase.from('cards').select('id', { count: 'exact', head: true }).eq('account_id', accountId),
      supabase.from('future_releases').select('id', { count: 'exact', head: true }).eq('account_id', accountId),
      supabase.from('installment_groups').select('id', { count: 'exact', head: true }).eq('account_id', accountId)
    ]);

    const counts = {
      transactions: transactions.count || 0,
      cards: cards.count || 0,
      futureReleases: futureReleases.count || 0,
      installmentGroups: installmentGroups.count || 0,
      total: 0
    };
    
    counts.total = counts.transactions + counts.cards + counts.futureReleases + counts.installmentGroups;
    
    return counts;
  } catch (error) {
    logger.error('Error getting account related data count', { error, accountId });
    throw error;
  }
}

/**
 * Delete an account and all its related data (cascade delete)
 */
export async function deleteAccountWithRelatedData(accountId: string): Promise<{
  deletedTransactions: number;
  deletedCards: number;
  deletedFutureReleases: number;
  deletedInstallmentGroups: number;
}> {
  const deletedCounts = {
    deletedTransactions: 0,
    deletedCards: 0,
    deletedFutureReleases: 0,
    deletedInstallmentGroups: 0
  };

  try {
    // Delete in order to avoid foreign key issues
    
    // 1. Delete transactions
    const { count: transactionsCount, error: transactionsError } = await supabase
      .from('transactions')
      .delete({ count: 'exact' })
      .eq('account_id', accountId);
    
    if (transactionsError) {
      logger.error('Error deleting transactions for account', { error: transactionsError, accountId });
      throw new Error('Erro ao excluir transações da conta');
    }
    deletedCounts.deletedTransactions = transactionsCount || 0;

    // 2. Delete future releases
    const { count: futureReleasesCount, error: futureReleasesError } = await supabase
      .from('future_releases')
      .delete({ count: 'exact' })
      .eq('account_id', accountId);
    
    if (futureReleasesError) {
      logger.error('Error deleting future releases for account', { error: futureReleasesError, accountId });
      throw new Error('Erro ao excluir lançamentos futuros da conta');
    }
    deletedCounts.deletedFutureReleases = futureReleasesCount || 0;

    // 3. Delete installment groups
    const { count: installmentGroupsCount, error: installmentGroupsError } = await supabase
      .from('installment_groups')
      .delete({ count: 'exact' })
      .eq('account_id', accountId);
    
    if (installmentGroupsError) {
      logger.error('Error deleting installment groups for account', { error: installmentGroupsError, accountId });
      throw new Error('Erro ao excluir grupos de parcelas da conta');
    }
    deletedCounts.deletedInstallmentGroups = installmentGroupsCount || 0;

    // 4. Delete cards associated with the account
    const { count: cardsCount, error: cardsError } = await supabase
      .from('cards')
      .delete({ count: 'exact' })
      .eq('account_id', accountId);
    
    if (cardsError) {
      logger.error('Error deleting cards for account', { error: cardsError, accountId });
      throw new Error('Erro ao excluir cartões da conta');
    }
    deletedCounts.deletedCards = cardsCount || 0;

    // 5. Finally delete the account itself
    const { error: accountError } = await supabase
      .from('accounts')
      .delete()
      .eq('id', accountId);
    
    if (accountError) {
      logger.error('Error deleting account', { error: accountError, accountId });
      throw new Error('Erro ao excluir a conta');
    }

    logger.info('Account deleted with all related data', { accountId, deletedCounts });
    
    return deletedCounts;
  } catch (error) {
    logger.error('Error in deleteAccountWithRelatedData', { error, accountId });
    throw error;
  }
}
